import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { push } from 'react-router-redux';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import { __ } from '@bart.sk-eshop/react-i18n/lib';
import AnimateHeight from 'react-animate-height';

import {
  capitalizeFirstLetter,
  getUrl,
  isHomepageEshopEnabled,
  isSkvelkoHomepageEnabled,
  isSsr,
} from '../../../../utilities';
import { getImagePath } from '../../../../utilities/product';
import {
  FURNITURE_CATEGORY_GROUP_UNIQID,
  getLoggedInMenu,
} from '../../../../constants';
import { setUpsellNewsletterModalOpen } from '../../../../containers/App/actions';
import API from '../../../../services/API';
import { Loader } from '../../../_helpers/Loader/Loader';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XIcon,
} from '../HeaderNew/icons';
import {
  audienceForUser,
  AUDIENCE_TABS,
  AudienceKey,
  buildSubmenu,
  buildUtilityLinks,
  detectAudienceStrict,
  isFurnitureSection,
  loadAudienceCookie,
  saveAudienceCookie,
  MenuNode,
  SubmenuItem,
} from '../HeaderNew/menuData';
import { getCategoryIcon } from './categoryIcons';

const ESHOP_KEY = 'eshop';
const FURNITURE_KEY = 'furniture';
const ACCOUNT_KEY = 'account';

export type DrawerView = 'menu' | 'account';

interface MobileMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  mainMenu: MenuNode[];
  categoriesGroup?: any[];
  user: any;
  dispatch: any;
  currentPath: string;
  currentUniqs: string[];
  currentCategoryGroupId?: number;
  currentCategoryId?: number;
  view?: DrawerView;
  upsellEligible?: boolean;
  settings?: any;
  queryAudience?: AudienceKey | null;
}

function getCategoryChildren(node: any): any[] {
  if (!node) {
    return [];
  }
  let children: any[] =
    (node.children && node.children.length && node.children) ||
    (node.categories && node.categories.length && node.categories) ||
    [];
  if (node.subgroups && node.subgroups.length) {
    const virtual = node.subgroups.map((sg: any) => ({
      category_id: `subgroup_${sg.id}`,
      category_name: sg.name,
      name: sg.name,
      url: sg.uniqid_url || null,
      image: sg.image_name || null,
      children: sg.categories || [],
    }));
    children = [...virtual, ...children];
  }
  return children;
}

function nodeName(node: any): string {
  return capitalizeFirstLetter(node.category_name || node.name || '');
}

function nodeUrl(node: any): string | null {
  if (!node) {
    return null;
  }
  return node.url || node.uniqid_url || null;
}

function categoryImage(node: any): string | null {
  const img = node && node.image;
  if (!img || img === 'catalog/null' || img === 'null') {
    return null;
  }
  return img;
}

const CategoryThumb: React.FC<{ node: any }> = ({ node }) => {
  const img = categoryImage(node);
  if (img) {
    return (
      <Thumb
        src={getImagePath(img, { width: 64, height: 64 })}
        alt=""
        loading="lazy"
      />
    );
  }
  const Icon = getCategoryIcon(nodeName(node));
  return <Icon className="cat-ico" />;
};

function renderItemLabel(label: string): React.ReactNode {
  const text = label || '';
  const i = text.toLowerCase().indexOf('skvelko');
  if (i === -1) {
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  }
  const before = text.slice(0, i).replace(/(&nbsp;|\s)+$/gi, '');
  const after = text.slice(i + 'skvelko'.length).replace(/^(&nbsp;|\s)+/gi, '');
  return (
    <SkvelkoLabel>
      {before ? <span dangerouslySetInnerHTML={{ __html: before }} /> : null}
      <img src="/images/menu-skvelko-logo.png" alt="Skvelko" />
      {after ? <span dangerouslySetInnerHTML={{ __html: after }} /> : null}
    </SkvelkoLabel>
  );
}

function MobileMenuDrawer({
  open,
  onClose,
  mainMenu,
  categoriesGroup = [],
  user,
  dispatch,
  currentPath,
  currentUniqs,
  currentCategoryGroupId,
  currentCategoryId,
  upsellEligible,
  settings,
  view = 'menu',
  queryAudience,
}: MobileMenuDrawerProps) {
  const [audience, setAudience] = React.useState<AudienceKey>(
    () =>
      queryAudience ??
      detectAudienceStrict(mainMenu, currentPath, currentUniqs) ??
      audienceForUser(user) ??
      'self',
  );

  React.useEffect(() => {
    if (!queryAudience) {
      return;
    }
    setAudience(queryAudience);
    saveAudienceCookie(queryAudience);
  }, [queryAudience]);

  React.useEffect(() => {
    if (
      queryAudience ||
      detectAudienceStrict(mainMenu, currentPath, currentUniqs) ||
      audienceForUser(user)
    ) {
      return;
    }
    const cookieAudience = loadAudienceCookie();
    if (cookieAudience) {
      setAudience(cookieAudience);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);
  const [drill, setDrill] = React.useState<any[]>([]);
  const [mode, setMode] = React.useState<DrawerView>(view);
  const [shops, setShops] = React.useState<any[] | null>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  const accountItems = React.useMemo(
    () => (user ? getLoggedInMenu(user) : []),
    [user],
  );

  const skvelkoFirst = isSkvelkoHomepageEnabled(settings);
  const eshopHomepage = isHomepageEshopEnabled(settings);
  const submenu: SubmenuItem[] = React.useMemo(
    () => buildSubmenu(mainMenu, audience, skvelkoFirst, eshopHomepage),
    [mainMenu, audience, skvelkoFirst, eshopHomepage],
  );
  const utilityLinks = React.useMemo(
    () => buildUtilityLinks(mainMenu),
    [mainMenu],
  );

  React.useEffect(() => {
    if (open) {
      setExpandedKey(null);
      setDrill([]);
      setMode(view);
    }
  }, [open]);

  React.useEffect(() => {
    const accountAudience = audienceForUser(user);
    if (!accountAudience || queryAudience) {
      return;
    }
    if (detectAudienceStrict(mainMenu, currentPath, currentUniqs)) {
      return;
    }
    setAudience(accountAudience);
    saveAudienceCookie(accountAudience);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const furnitureActive = isFurnitureSection(
    categoriesGroup,
    currentCategoryGroupId,
    currentCategoryId,
  );
  React.useEffect(() => {
    if (!furnitureActive || queryAudience) {
      return;
    }
    if (detectAudienceStrict(mainMenu, currentPath, currentUniqs)) {
      return;
    }
    setAudience('schools');
    saveAudienceCookie('schools');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [furnitureActive]);

  React.useEffect(() => {
    if (isSsr() || typeof document === 'undefined') {
      return;
    }
    if (open) {
      const prevBody = document.body.style.overflow;
      const prevHtml = document.documentElement.style.overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevBody;
        document.documentElement.style.overflow = prevHtml;
      };
    }
    return;
  }, [open]);

  React.useEffect(() => {
    if (isSsr() || typeof document === 'undefined') {
      return;
    }
    document.body.classList.toggle('mobile-menu-open', open);
    const smartsupp = (window as any).smartsupp;
    if (typeof smartsupp === 'function') {
      smartsupp(open ? 'chat:hide' : 'chat:show');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
      if (typeof (window as any).smartsupp === 'function') {
        (window as any).smartsupp('chat:show');
      }
    };
  }, [open]);

  const onPickAudience = (key: AudienceKey) => {
    setAudience(key);
    saveAudienceCookie(key);
    setExpandedKey(null);
    setDrill([]);
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  };

  const navigate = (url?: string | null) => {
    if (!url) {
      return;
    }
    dispatch(push(getUrl(url)));
    onClose();
  };

  const enterCategory = (node: any) => {
    if (getCategoryChildren(node).length) {
      setDrill((d) => [...d, node]);
    } else {
      navigate(nodeUrl(node));
    }
  };

  const enterLinkList = (link: any) => {
    setDrill((d) => [
      ...d,
      {
        __linkList: true,
        category_name: link.label,
        links: link.children || [],
      },
    ]);
  };

  const enterShops = (link: any) => {
    setDrill((d) => [
      ...d,
      {
        __shopList: true,
        category_name: link.label,
        allUrl: link.to,
      },
    ]);
    if (shops === null) {
      API.loadShops()
        .then((res: any) => setShops((res && res.shops) || []))
        .catch(() => setShops([]));
    }
  };

  const drillBack = () => setDrill((d) => d.slice(0, -1));

  const furnitureChildren = React.useMemo(() => {
    const group = (categoriesGroup || []).find(
      (g: any) => g.uniqid === FURNITURE_CATEGORY_GROUP_UNIQID,
    );
    return group ? getCategoryChildren(group) : [];
  }, [categoriesGroup]);

  const eshopGroups = React.useMemo(() => {
    return (categoriesGroup || []).filter(
      (g: any) => g.uniqid !== FURNITURE_CATEGORY_GROUP_UNIQID,
    );
  }, [categoriesGroup]);

  const current = drill.length ? drill[drill.length - 1] : null;
  const currentUrl = current ? nodeUrl(current) : null;

  return (
    <Overlay open={open} aria-hidden={!open}>
      <Header>
        <Title>{mode === 'account' ? __('Môj účet') : __('Menu')}</Title>
        <CloseButton type="button" onClick={onClose} aria-label={__('Zavrieť')}>
          <XIcon />
        </CloseButton>
      </Header>

      {mode === 'account' ? null : (
        <Tabs>
          {AUDIENCE_TABS.map((tab) => (
            <Tab
              key={tab.key}
              type="button"
              className={audience === tab.key ? 'active' : ''}
              onClick={() => onPickAudience(tab.key)}
            >
              {tab.label}
            </Tab>
          ))}
        </Tabs>
      )}

      <Body ref={bodyRef}>
        {mode === 'account' ? (
          <Drill>
            <DrillList>
              {accountItems.map((item) => (
                <AccountRow
                  as="button"
                  type="button"
                  key={item.url}
                  className={item.highlighted ? 'highlighted' : ''}
                  onClick={() => navigate(item.url)}
                >
                  <span className="row-label">{item.name}</span>
                  <ChevronRightIcon className="chev" />
                </AccountRow>
              ))}
            </DrillList>
          </Drill>
        ) : current ? (
          <Drill>
            <DrillHeader>
              <BackButton type="button" onClick={drillBack}>
                <ArrowLeftIcon />
              </BackButton>
              <DrillTitle>{nodeName(current)}</DrillTitle>
            </DrillHeader>
            {current.__shopList ? (
              <DrillList>
                <DrillRow
                  as="button"
                  type="button"
                  onClick={() => navigate(current.allUrl)}
                >
                  <span className="row-label">{__('Všetky predajne')}</span>
                  <ChevronRightIcon className="chev" />
                </DrillRow>
                {shops === null ? (
                  <ShopsLoading>
                    <Loader loading={true} dim={false} />
                  </ShopsLoading>
                ) : (
                  shops.map((shop: any) => (
                    <DrillRow
                      as="button"
                      type="button"
                      key={shop.id}
                      onClick={() => navigate(shop.url)}
                    >
                      <span className="row-label">{shop.name}</span>
                      <ChevronRightIcon className="chev" />
                    </DrillRow>
                  ))
                )}
              </DrillList>
            ) : current.__linkList ? (
              <DrillList>
                {(current.links || []).map((l: any) => (
                  <DrillRow
                    as="button"
                    type="button"
                    key={l.key}
                    onClick={() => navigate(l.to)}
                  >
                    <span
                      className="row-label"
                      dangerouslySetInnerHTML={{ __html: l.label }}
                    />
                    <ChevronRightIcon className="chev" />
                  </DrillRow>
                ))}
              </DrillList>
            ) : (
              <DrillList>
                {currentUrl ? (
                  <DrillRow
                    as="button"
                    type="button"
                    className="all"
                    onClick={() => navigate(currentUrl)}
                  >
                    <CategoryThumb node={current} />
                    <span className="row-label">{__('Všetky produkty')}</span>
                    <ChevronRightIcon className="chev" />
                  </DrillRow>
                ) : null}
                {getCategoryChildren(current).map((child) => {
                  const hasChildren = getCategoryChildren(child).length > 0;
                  return (
                    <DrillRow
                      as="button"
                      type="button"
                      key={child.category_id || child.id || nodeName(child)}
                      onClick={() => enterCategory(child)}
                    >
                      <CategoryThumb node={child} />
                      <span
                        className="row-label"
                        dangerouslySetInnerHTML={{ __html: nodeName(child) }}
                      />
                      {hasChildren ? (
                        <ChevronRightIcon className="chev" />
                      ) : null}
                    </DrillRow>
                  );
                })}
              </DrillList>
            )}
          </Drill>
        ) : (
          <>
            <Buttons>
              {submenu.map((item) => {
                const isEshop = item.key === ESHOP_KEY;
                const isFurniture =
                  item.key === FURNITURE_KEY && furnitureChildren.length > 0;
                const hasChildren = !!(item.children && item.children.length);
                const isAccordion =
                  isEshop || isFurniture || hasChildren ? true : false;
                const expanded = expandedKey === item.key;
                const catNodes = isEshop
                  ? eshopGroups
                  : isFurniture
                  ? furnitureChildren
                  : null;

                if (!isAccordion) {
                  return (
                    <ButtonLink key={item.key} to={item.to} onClick={onClose}>
                      {renderItemLabel(item.label)}
                    </ButtonLink>
                  );
                }

                return (
                  <AccordionCard
                    key={item.key}
                    className={expanded ? 'open' : ''}
                  >
                    <ButtonToggle
                      type="button"
                      className={expanded ? 'open' : ''}
                      onClick={() =>
                        setExpandedKey((k) =>
                          k === item.key ? null : item.key,
                        )
                      }
                    >
                      {renderItemLabel(item.label)}
                      <ChevronDownIcon className="chev" />
                    </ButtonToggle>
                    <AnimateHeight
                      height={expanded ? 'auto' : 0}
                      duration={200}
                    >
                      <AccordionList>
                        {catNodes
                          ? catNodes.map((cat) => {
                              const hasKids =
                                getCategoryChildren(cat).length > 0;
                              return (
                                <AccordionRow
                                  as="button"
                                  type="button"
                                  key={
                                    cat.category_id || cat.id || nodeName(cat)
                                  }
                                  onClick={() => enterCategory(cat)}
                                >
                                  <CategoryThumb node={cat} />
                                  <span
                                    className="row-label"
                                    dangerouslySetInnerHTML={{
                                      __html: nodeName(cat),
                                    }}
                                  />
                                  {hasKids ? (
                                    <ChevronRightIcon className="chev" />
                                  ) : null}
                                </AccordionRow>
                              );
                            })
                          : (item.children || []).map((child) => (
                              <AccordionRow
                                key={child.key}
                                to={child.to}
                                onClick={onClose}
                              >
                                <span
                                  className="row-label"
                                  dangerouslySetInnerHTML={{
                                    __html: child.label,
                                  }}
                                />
                                <ChevronRightIcon className="chev" />
                              </AccordionRow>
                            ))}
                      </AccordionList>
                    </AnimateHeight>
                  </AccordionCard>
                );
              })}

              {/* "Môj účet" section for logged-in users — the same account menu
                  added to the main mobile menu (mirrors the old frontend). */}
              {user ? (
                <AccordionCard
                  className={expandedKey === ACCOUNT_KEY ? 'open' : ''}
                >
                  <ButtonToggle
                    type="button"
                    className={expandedKey === ACCOUNT_KEY ? 'open' : ''}
                    onClick={() =>
                      setExpandedKey((k) =>
                        k === ACCOUNT_KEY ? null : ACCOUNT_KEY,
                      )
                    }
                  >
                    {__('Môj účet')}
                    <ChevronDownIcon className="chev" />
                  </ButtonToggle>
                  <AnimateHeight
                    height={expandedKey === ACCOUNT_KEY ? 'auto' : 0}
                    duration={200}
                  >
                    <AccordionList>
                      {accountItems.map((item) => (
                        <AccordionRow
                          as="button"
                          type="button"
                          key={item.url}
                          className={item.highlighted ? 'highlighted' : ''}
                          onClick={() => navigate(item.url)}
                        >
                          <span className="row-label">{item.name}</span>
                          <ChevronRightIcon className="chev" />
                        </AccordionRow>
                      ))}
                    </AccordionList>
                  </AnimateHeight>
                </AccordionCard>
              ) : null}
            </Buttons>

            {upsellEligible ? (
              <Promo>
                <PromoIcon
                  src="/images/menu-promo-discount.png"
                  alt=""
                  aria-hidden="true"
                />
                <PromoText>
                  <strong>{__('Zľava 20%')}</strong>
                  <span>{__('na prvý nákup')}</span>
                </PromoText>
                <PromoButton
                  as="button"
                  type="button"
                  onClick={() => {
                    dispatch(setUpsellNewsletterModalOpen(true));
                    onClose();
                  }}
                >
                  {__('Mám záujem')}
                </PromoButton>
              </Promo>
            ) : null}

            <Utility>
              {utilityLinks.map((link) => {
                const LinkIcon = link.icon;
                const isShops = link.key === 'shops';
                if (isShops || (link.children && link.children.length)) {
                  return (
                    <UtilityRow
                      as="button"
                      type="button"
                      key={link.key}
                      onClick={() =>
                        isShops ? enterShops(link) : enterLinkList(link)
                      }
                    >
                      <LinkIcon className="util-ico" />
                      <span>{link.label}</span>
                      <ChevronRightIcon className="chev" />
                    </UtilityRow>
                  );
                }
                return (
                  <UtilityRow key={link.key} to={link.to} onClick={onClose}>
                    <LinkIcon className="util-ico" />
                    <span>{link.label}</span>
                    <ChevronRightIcon className="chev" />
                  </UtilityRow>
                );
              })}
            </Utility>
          </>
        )}
      </Body>
    </Overlay>
  );
}

const Overlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1300;
  background: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.25s ease, visibility 0s linear 0.25s;
  ${({ open }) =>
    open &&
    css`
      transform: translateX(0);
      visibility: visible;
      transition: transform 0.25s ease;
    `}
  /* The drawer is a phone/tablet concept only — never show it on desktop. */
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${rem(16)};
  height: ${rem(56)};
`;

const Title = styled.span`
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(18)};
  font-weight: 700;
  line-height: 1;
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(36)};
  height: ${rem(36)};
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.18);
  color: ${({ theme }) => theme.color.white};
  svg {
    width: ${rem(22)};
    height: ${rem(22)};
  }
`;

const Tabs = styled.div`
  flex: 0 0 auto;
  background: ${({ theme }) => theme.color.primary};
  display: flex;
  align-items: stretch;
  gap: ${rem(2)};
  padding: ${rem(8)} ${rem(12)} 0;
`;

const Tab = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  flex: 1 1 0;
  min-width: 0;
  font-family: inherit;
  background: transparent;
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(13)};
  font-weight: 600;
  line-height: 1.15;
  text-align: center;
  padding: ${rem(12)};
  border-radius: ${rem(6)} ${rem(6)} 0 0;

  &.active {
    background: ${({ theme }) => theme.color.white};
    color: #171717;
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fafafa;
  padding: ${rem(16)} ${rem(16)} ${rem(32)};
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`;

const SkvelkoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  img {
    height: ${rem(22)};
    width: auto;
    display: block;
  }
`;

const buttonBase = css`
  appearance: none;
  border: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(16)};
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  font-size: ${rem(15)};
  font-weight: 600;
  line-height: 1.6;
  border-radius: ${rem(8)};
  padding: ${rem(16)} ${rem(24)};
  text-decoration: none;
  &:hover {
    text-decoration: none;
    color: #171717;
  }
`;

const ButtonLink = styled(Link)`
  ${buttonBase};
`;

const AccordionCard = styled.div`
  border-radius: ${rem(8)};
  &.open {
    background: ${({ theme }) => theme.color.white};
    box-shadow: 0 0 ${rem(24)} 0 rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
`;

const ButtonToggle = styled.button`
  ${buttonBase};
  position: relative;
  .chev {
    width: ${rem(22)};
    height: ${rem(22)};
    flex: 0 0 auto;
    transition: transform 0.2s ease;
  }
  /* Expanded: the yellow button becomes the white card's header — red label,
     square bottom corners, and a yellow underline inset from the edges. */
  &.open {
    background: transparent;
    color: ${({ theme }) => theme.color.primary};
    font-weight: 700;
    border-radius: ${rem(8)} ${rem(8)} 0 0;
  }
  &.open .chev {
    transform: rotate(180deg);
    color: #171717;
  }
  &.open::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${rem(2)};
    background: ${({ theme }) => theme.color.secondary};
  }
`;

const AccordionList = styled.div`
  background: ${({ theme }) => theme.color.white};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(8)} 0 ${rem(16)};
`;

const rowBase = css`
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(8)};
  color: #171717;
  font-size: ${rem(15)};
  font-weight: 400;
  line-height: 1.6;
  padding: ${rem(8)} ${rem(24)};
  text-decoration: none;
  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.color.primary};
  }
  .row-label {
    flex: 1 1 auto;
    min-width: 0;
  }
  .cat-ico {
    width: ${rem(32)};
    height: ${rem(32)};
    flex: 0 0 auto;
    color: ${({ theme }) => theme.color.primary};
  }
  .chev {
    width: ${rem(16)};
    height: ${rem(16)};
    flex: 0 0 auto;
    margin-left: auto;
    color: #c4c4c4;
  }
`;

const Thumb = styled.img`
  width: ${rem(32)};
  height: ${rem(32)};
  flex: 0 0 auto;
  border-radius: 0;
  object-fit: cover;
  background: #f4f4f4;
`;

const AccordionRow = styled(Link)`
  ${rowBase};
  /* "Odhlásiť sa" (logout) is visually emphasised in red. */
  &.highlighted {
    color: #bd1933;
    font-weight: 600;
  }
`;

const Promo = styled.div`
  margin-top: ${rem(16)};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(8)};
  background: ${({ theme }) => theme.color.secondary};
  border-radius: ${rem(8)};
  padding: ${rem(16)};
`;

const PromoIcon = styled.img`
  flex: 0 0 auto;
  width: ${rem(48)};
  height: ${rem(48)};
  object-fit: contain;
`;

const PromoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  color: #171717;
  line-height: 1.3;
  strong {
    font-size: ${rem(16)};
    font-weight: 700;
  }
  span {
    font-size: ${rem(14)};
    font-weight: 600;
  }
`;

const PromoButton = styled(Link)`
  /* Renders as a <button> (opens the coupon modal); reset native button chrome
     so it looks identical to the former link tile. */
  appearance: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  flex: 0 0 auto;
  margin-left: auto;
  background: ${({ theme }) => theme.color.primary};
  color: ${({ theme }) => theme.color.white};
  font-size: ${rem(15)};
  font-weight: 600;
  border-radius: ${rem(999)};
  padding: ${rem(8)} ${rem(16)};
  text-decoration: none;
  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.color.white};
  }
`;

const Utility = styled.div`
  margin-top: ${rem(16)};
`;

const UtilityRow = styled(Link)`
  /* Reset button chrome so the row looks identical whether it renders as a
     <Link> (direct links) or a <button> (Predajne/Pomoc/Kontakt drill down). */
  appearance: none;
  cursor: pointer;
  width: 100%;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  background: none;
  border: none;
  border-top: ${rem(1)} solid #e6e6e6;
  padding: ${rem(14)} ${rem(4)};
  color: #171717;
  font-size: ${rem(16)};
  font-weight: 400;
  text-decoration: none;
  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.color.primary};
  }
  .util-ico {
    width: ${rem(24)};
    height: ${rem(24)};
    flex: 0 0 auto;
    color: ${({ theme }) => theme.color.primary};
  }
  .chev {
    width: ${rem(16)};
    height: ${rem(16)};
    margin-left: auto;
    color: #9b9b9b;
  }
`;

const Drill = styled.div``;

const DrillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  padding-bottom: ${rem(12)};
  margin-bottom: ${rem(8)};
  border-bottom: ${rem(2)} solid ${({ theme }) => theme.color.secondary};
`;

const BackButton = styled.button`
  appearance: none;
  border: none;
  cursor: pointer;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: 50%;
  background: ${({ theme }) => theme.color.secondary};
  color: #171717;
  svg {
    width: ${rem(22)};
    height: ${rem(22)};
  }
`;

const DrillTitle = styled.div`
  font-size: ${rem(16)};
  font-weight: 700;
  color: ${({ theme }) => theme.color.primary};
  line-height: 1.5;
`;

const DrillList = styled.div`
  background: transparent;
`;

const DrillRow = styled(Link)`
  ${rowBase};
  border-top: ${rem(1)} solid #e6e6e6;
  &:first-child {
    border-top: none;
  }
  &.all {
    color: #bd1933;
    font-weight: 400;
  }
`;

const ShopsLoading = styled.div`
  position: relative;
  min-height: ${rem(96)};
`;

const AccountRow = styled(DrillRow)`
  &.highlighted {
    color: #bd1933;
    font-weight: 600;
  }
`;

const mapStateToProps = (state) => ({
  upsellEligible: state.general.upsellNewsletterEligible,
  settings: state.general.settings,
});

export default connect(mapStateToProps)(MobileMenuDrawer);
